// Vendor Program v2 PR 3 — the VG-M1 "Monthly Vendor Goal Progress" produce
// card on the loan Goals tab (the series ALSO appears on the Financial
// Reporting tab automatically — same machinery, same instances; the DW-M1
// DwReportCard pattern). Preparer path rides the VG-scoped endpoints
// (Vendors.Manage — E1-VM holds no Loans.Manage); reviewer signature stays
// FinancialReporting.Review. Deep-linkable: ?tab=goals&vgReport=<id> opens
// the instance's preview drawer.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import type { LoanReportDto } from "@/api/portfolioAdmin/loanReportsApi";
import { VG_SERIES_CODE } from "@/api/portfolioAdmin/financialReportingApi";
import {
  useCertifyReportInstance,
  useCreateVgReportInstance,
  useFileReportInstance,
  useLoanReportingOverview,
  useSubmitVgReportInstance,
} from "@/hooks/portfolioAdmin/useFinancialReporting";
import { CreateReportModal } from "@/components/portfolioAdmin/reporting/CreateReportModal";
import { SeriesReportPreviewDrawer } from "@/components/portfolioAdmin/reporting/SeriesReportPreviewDrawer";
import { LoanReportStatusBadge } from "@/components/portfolioAdmin/loanDetail/reports/LoanReportLedger";
import { reportingPeriodLabel } from "@/components/portfolioAdmin/reporting/reportingFormat";
import { formatCalendarDate } from "@/utils/datetime";

const DUE_BADGE: Record<string, "draft" | "review" | "error"> = {
  upcoming: "draft",
  due: "review",
  overdue: "error",
};

export function VgM1ProduceCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const toast = useToast();
  const overview = useLoanReportingOverview(loanId);

  const create = useCreateVgReportInstance(loanId);
  const submit = useSubmitVgReportInstance(loanId);
  const certify = useCertifyReportInstance(loanId);
  const file = useFileReportInstance(loanId);

  const [createOpen, setCreateOpen] = useState(false);
  const [openReport, setOpenReportState] = useState<LoanReportDto | null>(null);
  const [params, setParams] = useSearchParams();

  const canPrepare =
    auth.hasPermission("Vendors.Manage") || auth.hasRoleCode("ADMIN");
  const canReview =
    auth.hasPermission("FinancialReporting.Review") || auth.hasRoleCode("ADMIN");

  const card = useMemo(
    () => overview.data?.series.find((s) => s.code === VG_SERIES_CODE) ?? null,
    [overview.data],
  );
  const instances = useMemo(
    () =>
      (overview.data?.instances ?? []).filter((i) => i.seriesCode === VG_SERIES_CODE),
    [overview.data],
  );

  /** Open/close the preview drawer and mirror the id into ?vgReport=. */
  const setOpenReport = (report: LoanReportDto | null) => {
    setOpenReportState(report);
    const updated = new URLSearchParams(params);
    if (report) updated.set("vgReport", report.id);
    else updated.delete("vgReport");
    setParams(updated, { replace: true });
  };

  // Deep-link entry: ?vgReport=<id> opens the matching instance once the
  // overview has loaded (unknown ids are ignored, never a broken drawer).
  const deepLinkId = params.get("vgReport");
  useEffect(() => {
    if (!deepLinkId || openReport) return;
    const match = instances.find((i) => i.id === deepLinkId);
    if (match) setOpenReportState(match);
  }, [deepLinkId, instances, openReport]);

  if (overview.isLoading) {
    return (
      <Card data-testid="vg-report-card">
        <p className="text-small text-gray-mid italic m-0">Loading report status…</p>
      </Card>
    );
  }
  if (overview.isError) {
    return (
      <Card data-testid="vg-report-card">
        <p className="text-small text-status-error m-0" role="alert">
          Could not load the monthly goal-progress report status.{" "}
          <button
            type="button"
            className="text-gold-shadow hover:underline"
            onClick={() => void overview.refetch()}
            data-testid="vg-report-retry"
          >
            Retry
          </button>
        </p>
      </Card>
    );
  }
  // Series not yet active/seeded in this environment — render nothing
  // rather than a broken card (the DwReportCard convention).
  if (!card) return null;

  const doCreate = async (narrative: string | null) => {
    try {
      const draft = await create.mutateAsync({
        periodStart: card.nextPeriodStart,
        periodEnd: card.nextPeriodEnd,
        narrative,
      });
      setCreateOpen(false);
      setOpenReport(draft);
    } catch (err) {
      // The modal shows its own error; the one-click path needs a toast.
      if (!createOpen) toast.error(extractErrorMessage(err));
      throw err;
    }
  };

  return (
    <Card data-testid="vg-report-card">
      <div className="flex flex-col gap-space-2">
        <div className="flex items-start justify-between gap-space-2">
          <div className="min-w-0">
            <h3
              className="font-display font-bold text-h4 m-0 truncate"
              title="Monthly goal-progress report (VG-M1)"
            >
              Monthly goal-progress report (VG-M1)
            </h3>
            <p className="m-0 text-caption text-gray-mid">
              {reportingPeriodLabel(card.cadence, card.nextPeriodEnd)} • due{" "}
              {formatCalendarDate(card.nextDueDate)}
            </p>
          </div>
          <span className="shrink-0">
            <Badge status={DUE_BADGE[card.dueStatus] ?? "draft"}>{card.dueStatus}</Badge>
          </span>
        </div>
        {canPrepare && (
          <div className="flex flex-wrap items-center gap-space-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => doCreate(null).catch(() => undefined)}
              disabled={create.isPending}
              data-testid="vg-report-create"
            >
              {create.isPending ? "Creating…" : "Create report"}
            </Button>
            <Button
              variant="text"
              size="sm"
              onClick={() => setCreateOpen(true)}
              data-testid="vg-report-create-with-narrative"
            >
              With narrative…
            </Button>
          </div>
        )}

        {instances.length === 0 ? (
          <p className="m-0 text-small text-gray-mid italic">
            No VG-M1 reports filed yet. The monthly filing aggregates every
            vendor&apos;s goal progress on this loan, grouped by goal.
          </p>
        ) : (
          <ul className="m-0 p-0 list-none" data-testid="vg-report-ledger">
            {instances.map((i) => (
              <li key={i.id} className="border-b border-border-light last:border-0">
                <button
                  type="button"
                  onClick={() => setOpenReport(i)}
                  className="w-full text-left py-space-2 flex items-center gap-space-3 hover:bg-white-warm rounded"
                  data-testid={`vg-report-row-${i.id}`}
                >
                  <span className="text-small font-bold">
                    {reportingPeriodLabel("Monthly", i.periodEnd)}
                  </span>
                  <LoanReportStatusBadge status={i.status} />
                  <span className="text-caption text-gray-mid ml-auto">
                    {i.preparedByUserName ?? ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {createOpen && (
        <CreateReportModal
          card={card}
          open
          onClose={() => setCreateOpen(false)}
          onCreate={doCreate}
          isPending={create.isPending}
        />
      )}

      {openReport && (
        <SeriesReportPreviewDrawer
          report={openReport}
          seriesTitle={card.title}
          open
          onClose={() => setOpenReport(null)}
          canPrepare={canPrepare}
          canReview={canReview}
          onSubmit={async (id) => setOpenReport(await submit.mutateAsync(id))}
          onCertify={async (id) => setOpenReport(await certify.mutateAsync(id))}
          onFile={async (id) => setOpenReport(await file.mutateAsync(id))}
          isPending={submit.isPending || certify.isPending || file.isPending}
        />
      )}
    </Card>
  );
}
